import { useMemo } from 'react';
import { HybridClassSelect } from './HybridClassSelect';
import { InlineChoiceList } from './InlineChoiceList';
import { MultiChoiceList } from './MultiChoiceList';
import {
    SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID,
    getEffectiveCategoriesForAnnotator,
    getFreeTextOptionLabelForDisplay,
    shouldUseInlineChoices,
} from './slideLevelClassificationModel';
import styles from './TopBarControls.module.css';

/**
 * Answer control for one slide-level / survey question (discrete / hybrid / free-text / multi).
 *
 * @param {'panel' | 'topBar'} [layout] - panel: inline list when few choices; topBar: always collapsed control
 * @param {'popover' | 'inline'} [freeTextPresentation] - popover (viewer default) or always-visible field
 */
export function QuestionAnswerControls({
    question,
    categoryId = '',
    categoryIds = [],
    freeTextAnswer = '',
    disabled = false,
    labelledById,
    layout = 'panel',
    freeTextPresentation = 'popover',
    onCommitSelection,
    onToggleMultiCategory,
    onActivateMultiOther,
    onFreeTextChange,
    onFreeTextInlineFocus,
    onFreeTextInlineBlur,
    onOpenFreeTextPopover,
}) {
    const {
        categories = [],
        allowDiscreteCategories,
        allowFreeText,
        allowMultiple = false,
        freeText: freeTextSettings,
    } = question;

    const freeTextOptionLabel = getFreeTextOptionLabelForDisplay(
        freeTextSettings?.optionLabel,
    );
    const freePlaceholder = freeTextSettings?.placeholder ?? '';
    const freeMultiline = Boolean(freeTextSettings?.multiline);
    const inlineFt = freeTextPresentation === 'inline';

    const multi = Boolean(allowMultiple) && allowDiscreteCategories;
    const discreteOnly = allowDiscreteCategories && !allowFreeText && !multi;
    const freeOnly = !allowDiscreteCategories && allowFreeText;
    const hybrid = allowDiscreteCategories && allowFreeText && !multi;
    const useInline = layout === 'panel' && shouldUseInlineChoices(question);

    const effectiveMultiCategories = useMemo(
        () => (multi ? getEffectiveCategoriesForAnnotator(question) : []),
        [multi, question],
    );

    const selectValue = useMemo(() => {
        const id = String(categoryId);
        if (!id) return '';
        if (id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) return SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID;
        if (categories.some((c) => String(c.id) === id)) return id;
        return '';
    }, [categoryId, categories]);

    const controlDisabled =
        disabled
        || (!allowDiscreteCategories && !allowFreeText);

    const hybridDisabled =
        disabled
        || !hybrid
        || categories.length === 0;

    const otherSelectedMulti = Array.isArray(categoryIds)
        && categoryIds.map(String).includes(SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID);
    const otherSelectedHybrid = String(categoryId) === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID;
    const showInlineOtherField = inlineFt && allowFreeText && (
        freeOnly || otherSelectedMulti || otherSelectedHybrid
    );

    function handleSelectChange(e) {
        onCommitSelection(e.target.value);
    }

    function renderInlineFreeTextField() {
        if (!showInlineOtherField) return null;
        const Field = freeMultiline ? 'textarea' : 'input';
        return (
            <Field
                {...(freeMultiline ? { rows: 3 } : { type: 'text' })}
                className={freeMultiline ? styles.freeTextArea : styles.freeTextInline}
                value={freeTextAnswer}
                placeholder={freePlaceholder || undefined}
                disabled={controlDisabled}
                onChange={(e) => onFreeTextChange(e.target.value)}
                onFocus={onFreeTextInlineFocus}
                onBlur={onFreeTextInlineBlur}
                aria-label={freeTextOptionLabel}
            />
        );
    }

    const wrapClass = inlineFt
        ? `${styles.answerControls} ${styles.answerControlsStacked}`
        : styles.answerControls;

    if (multi) {
        return (
            <div className={wrapClass}>
                <MultiChoiceList
                    categories={effectiveMultiCategories}
                    selectedIds={categoryIds}
                    freeTextAnswer={inlineFt ? '' : freeTextAnswer}
                    disabled={controlDisabled || categories.length === 0}
                    labelledById={labelledById}
                    onToggleCategory={onToggleMultiCategory}
                    onActivateOther={() => {
                        if (inlineFt) {
                            if (!otherSelectedMulti) {
                                onToggleMultiCategory?.(
                                    SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID,
                                    freeTextOptionLabel,
                                );
                            }
                            return;
                        }
                        onActivateMultiOther?.();
                    }}
                />
                {renderInlineFreeTextField()}
            </div>
        );
    }

    if (discreteOnly && useInline) {
        return (
            <div className={wrapClass}>
                <InlineChoiceList
                    categories={categories}
                    value={selectValue}
                    categoryId={categoryId}
                    freeTextAnswer={freeTextAnswer}
                    freeTextOptionLabel={freeTextOptionLabel}
                    includeFreeText={false}
                    disabled={controlDisabled || categories.length === 0}
                    labelledById={labelledById}
                    onCommitSelection={onCommitSelection}
                />
            </div>
        );
    }

    if (hybrid && useInline) {
        return (
            <div className={wrapClass}>
                <InlineChoiceList
                    categories={categories}
                    value={selectValue}
                    categoryId={categoryId}
                    freeTextAnswer={inlineFt ? '' : freeTextAnswer}
                    freeTextOptionLabel={freeTextOptionLabel}
                    includeFreeText
                    disabled={hybridDisabled}
                    labelledById={labelledById}
                    onCommitSelection={onCommitSelection}
                />
                {renderInlineFreeTextField()}
            </div>
        );
    }

    return (
        <div className={wrapClass}>
            {discreteOnly && (
                <select
                    className={styles.select}
                    aria-labelledby={labelledById}
                    value={selectValue}
                    onChange={handleSelectChange}
                    disabled={controlDisabled || categories.length === 0}
                >
                    <option value="">— Select —</option>
                    {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.label?.trim() ? c.label : c.id}
                        </option>
                    ))}
                </select>
            )}

            {hybrid && categories.length > 0 ? (
                <HybridClassSelect
                    categories={categories}
                    value={selectValue}
                    categoryId={categoryId}
                    freeTextAnswer={inlineFt ? '' : freeTextAnswer}
                    freeTextOptionLabel={freeTextOptionLabel}
                    disabled={hybridDisabled}
                    ariaLabelledBy={labelledById}
                    onCommitSelection={onCommitSelection}
                />
            ) : null}

            {hybrid && inlineFt ? renderInlineFreeTextField() : null}

            {freeOnly && inlineFt && renderInlineFreeTextField()}

            {freeOnly && !inlineFt && !freeMultiline && (
                <input
                    type="text"
                    className={styles.freeTextInline}
                    value={freeTextAnswer}
                    placeholder={freePlaceholder || undefined}
                    disabled={controlDisabled}
                    onChange={(e) => onFreeTextChange(e.target.value)}
                    onFocus={onFreeTextInlineFocus}
                    onBlur={onFreeTextInlineBlur}
                    aria-labelledby={labelledById}
                />
            )}

            {freeOnly && !inlineFt && freeMultiline && (
                <button
                    type="button"
                    className={styles.enterValueButton}
                    disabled={controlDisabled}
                    onClick={onOpenFreeTextPopover}
                >
                    {String(freeTextAnswer).trim() ? 'Edit answer' : 'Enter a value'}
                </button>
            )}

            {hybrid && !inlineFt && otherSelectedHybrid && !freeMultiline ? (
                <input
                    type="text"
                    className={styles.freeTextInline}
                    value={freeTextAnswer}
                    placeholder={freePlaceholder || undefined}
                    disabled={controlDisabled}
                    onChange={(e) => onFreeTextChange(e.target.value)}
                    onFocus={onFreeTextInlineFocus}
                    onBlur={onFreeTextInlineBlur}
                    aria-label={freeTextOptionLabel}
                />
            ) : null}
        </div>
    );
}
